import React from 'react';
import { flowRight } from 'lodash/fp';
import { Link } from 'react-router-dom';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import { withStyles } from '@material-ui/core/styles';
import formatters from '../../../../formatters';
import Access from 'ui/Access';
import { MTable } from 'ui/mtable/MTable';
import EditButton from 'ui/EditButton';
import DetailsButton from 'ui/DetailsButton';
import StatusChip from 'ui/StatusChip';
import StatusIcon from 'ui/StatusIcon';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import UserInfo from 'ui/user/UserInfo';
import { groupBy } from 'utils/collections';

import WhiteBack from '../../../WhiteBack';
import { withAppContext } from '../../../AppContext';
import {
    removeUsers,
    loadUsers,
    removeUsersAccess,
    enableUsersAccess,
} from './services';

const usersStyles = theme => ({
    button: {
        margin: theme.spacing(),
        fontSize: '10px',
    },
    leftIcon: {
        marginRight: theme.spacing(),
        fontSize: '15px',
    },
});

const ADD_USER = 'addUser';
const EDIT_USER = 'editUser';
const USER_DETAILS = 'userDetails';
const REMOVE_PARTNER_ACCOUNT = 'removePartnerAccount';
const REMOVE_ACCESS = 'removeAccess';
const ENABLE_ACCESS = 'enableAccess';

class Users extends React.Component {
    state = {
        users: [],
        usersLoading: true,
        selectedUsers: [],
        currentAction: null,
        actionResultName: null,
    };

    componentDidMount() {
        this.fetchUsers();
    }

    componentDidUpdate = (prevProps, prevState) => {
        const hadError =
            prevProps.formErrors !== this.props.formErrors &&
            this.props.formErrors == null;
        const withoutError =
            prevProps.formErrors === this.props.formErrors &&
            prevState.currentAction !== null;
        if (!this.props.currentUser && (withoutError || hadError)) {
            this.setState({ currentAction: null });
        }
    };

    fetchUsers() {
        const { companyId } = this.props;
        this.setState({
            users: [],
            usersLoading: true,
        });
        loadUsers(companyId)
            .then(data => {
                this.setState({
                    users: data,
                    usersLoading: false,
                });
            })
            .catch(() => {
                this.setState({
                    usersLoading: false,
                });
            });
    }
    handleRemoveAccount = users => {
        const { companyId } = this.props;
        removeUsers(users, companyId).then(json => {
            if (json.error) {
                console.log(json.error);
            }
            this.fetchUsers();
        });
    };

    handleRemoveAccess = users => {
        const { companyId } = this.props;
        removeUsersAccess(users, companyId).then(json => {
            if (json.error) {
                console.log(json.error);
            }
            this.fetchUsers();
        });
    };

    handleEnableAccess = users => {
        const { companyId } = this.props;
        enableUsersAccess(users, companyId).then(json => {
            if (json.error) {
                console.log(json.error);
            }
            this.fetchUsers();
        });
    };
    getAvailableActions = (selectedUsers, actions) => {
        const managedBy = Object.keys(groupBy(selectedUsers, 'managed'));
        let selectedStatuses = Object.keys(groupBy(selectedUsers, 'status'));
        selectedStatuses = selectedStatuses.concat(managedBy);
        selectedStatuses = new Set(selectedStatuses.sort());
        const actionFilters = action => {
            if (action.actionFilter) {
                return selectedUsers.every(user => action.actionFilter(user));
            }
            return true;
        };
        const availableActions = actions
            .filter(action => {
                const notAllowedUsersStatuses =
                    action.notAllowedUsersStatuses || [];
                const some = notAllowedUsersStatuses.filter(status => {
                    return selectedStatuses.has(status);
                });
                return some.length === 0;
            })
            .filter(actionFilters);
        return availableActions;
    };

    getActions = props => {
        return [
            {
                key: REMOVE_PARTNER_ACCOUNT,
                name: 'Delete Partner Portal Account',
                action: this.handleAction(REMOVE_PARTNER_ACCOUNT),
            },
            {
                key: REMOVE_ACCESS,
                name: 'Disable Partner Portal Account',
                action: this.handleAction(REMOVE_ACCESS),
                actionFilter(user) {
                    return user.enabled && user.partner_portal_access;
                },
            },
            {
                key: ENABLE_ACCESS,
                name: 'Enable Partner Portal Account',
                action: this.handleAction(ENABLE_ACCESS),
                actionFilter(user) {
                    return !user.enabled || !user.partner_portal_access;
                },
            },
        ];
    };

    getColumns = editable => {
        const { companyId } = this.props;
        return [
            {
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                export: false,
                notFilter: true,
            },
            {
                field: 'email',
                title: 'User',
                render: row => <UserInfo user={row} />,
                cellStyle: {
                    minWidth: '400px',
                },
            },
            {
                field: 'partner_portal_username',
                title: 'Username',
                cellStyle: {
                    minWidth: '170px',
                },
            },
            {
                field: 'roles',
                title: 'Roles',
                render: row => {
                    const names = row.roles.map(role => role.name).join(', ');
                    return <span>{names}</span>;
                },
                exportValue: val => val.map(role => role.name).join(', '),
                filter: (value, filter) =>
                    value.find(item =>
                        item.name
                            .toLowerCase()
                            .includes(filter.value.toLowerCase())
                    ),
                cellStyle: {
                    minWidth: '10%',
                },
            },
            {
                field: 'company_groups',
                title: 'Company Groups',
                render: row => {
                    const names = row.company_groups
                        .map(group => group.name)
                        .join(', ');
                    return <span>{names}</span>;
                },
                exportValue: val => val.map(group => group.name).join(', '),
                filter: (value, filter) =>
                    value.find(item =>
                        item.name
                            .toLowerCase()
                            .includes(filter.value.toLowerCase())
                    ),
                cellStyle: {
                    minWidth: '10%',
                },
            },
            {
                field: 'partner_portal_mfa',
                title: '2FA',
                render: row => <StatusIcon active={row.partner_portal_mfa} />,
                filterType: 'boolean',
                cellStyle: {
                    minWidth: '100px',
                },
            },
            {
                field: 'last_login',
                title: 'Last Login',
                render: rowData => formatters.date(rowData.partner_last_login),
                cellStyle: {
                    minWidth: '170px',
                },
            },
            {
                field: 'action',
                title: '',
                notFilter: true,
                export: false,
                render: ({ uuid }) => {
                    if (editable) {
                        return (
                            <EditButton
                                component={Link}
                                to={`/companies/${companyId}/partner/access/edit/${uuid}`}
                            />
                        );
                    } else {
                        return (
                            <DetailsButton
                                component={Link}
                                to={`/companies/${companyId}/partner/access/view/${uuid}`}
                            />
                        );
                    }
                },
            },
        ];
    };

    handleAction = currentAction => selectedUsers => {
        this.setState({
            currentAction,
            selectedUsers,
        });
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.setState({ currentAction: null, actionResultName }, () =>
            callback(this.state.selectedUsers, Boolean(actionResultName))
        );
    };

    handleAddUser = () => {
        this.setState({ currentAction: ADD_USER });
    };

    getAddUsersActions = () => (
        <Access.Change permission={Access.COMPANIES_PARTNER_USERS}>
            <MenuList>
                <MenuItem
                    component={Link}
                    to={`/companies/${this.props.companyId}/partner/access/new`}
                >
                    Add user
                </MenuItem>
            </MenuList>
        </Access.Change>
    );

    handleEdit = user => () => {
        this.setState(
            { currentAction: EDIT_USER },
            this.props.onFetchUser(user)
        );
    };
    handleDetails = user => () => {
        this.setState(
            { currentAction: USER_DETAILS },
            this.props.onFetchUser(user)
        );
    };

    renderDialog = () => {
        const { currentAction } = this.state;
        const dialog = {
            [REMOVE_PARTNER_ACCOUNT]: {
                title: 'Delete Partner Portal Account',
                subtitle1:
                    'This will remove access for the user(s) to access the Partner Portal. This does not change or delete a users access to the CyberFOX Password Manager app or to the customer portal.',
                warning: 'Are you sure you want to remove access for:',
            },
            [REMOVE_ACCESS]: {
                title: 'Disable Partner Portal Account',
                subtitle1:
                    'This will disable access for the user(s) to access the Partner Portal. This does not change or delete a users access to the CyberFOX Password Manager app or to the customer portal.',
                warning: 'Are you sure you want to disable access for:',
            },
            [ENABLE_ACCESS]: {
                title: 'Enable Partner Portal Account',
                subtitle1:
                    'This will enable access for the user(s) to access the Partner Portal.',
                warning: 'Are you sure you want to enable access for:',
            },
        };

        const callbacks = {
            [REMOVE_PARTNER_ACCOUNT]: { callback: this.handleRemoveAccount },
            [REMOVE_ACCESS]: { callback: this.handleRemoveAccess },
            [ENABLE_ACCESS]: { callback: this.handleEnableAccess },
        };

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedUsers.map(
                        item =>
                            `${item.first_name} ${item.last_name} - ${item.partner_portal_username}`
                    )}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    getActionMenuItem = selectedUsers => {
        const actions = this.getActions(this.props);
        const availableActions = this.getAvailableActions(
            selectedUsers,
            actions
        );

        const menuItems =
            availableActions.length > 0
                ? availableActions.map(action => ({
                      key: action.key,
                      name: action.name,
                      onClick: dropdownCloseCallback => {
                          action.action(selectedUsers);
                          dropdownCloseCallback();
                      },
                  }))
                : [
                      {
                          key: 'no',
                          name: 'No common action',
                          onClick: dropdownCloseCallback => {
                              dropdownCloseCallback();
                          },
                      },
                  ];

        return menuItems;
    };

    handleSelectionChange = val => {
        this.setState({ selectedUsers: val });
    };

    render() {
        const { users, usersLoading, selectedUsers } = this.state;
        const { app } = this.props;
        const isEditable = app.canChange(Access.COMPANIES_PARTNER_USERS);
        const columns = this.getColumns(isEditable);

        const selectedActions = this.getActionMenuItem(selectedUsers);

        return (
            <React.Fragment>
                <WhiteBack />
                {this.state.currentAction && this.renderDialog()}
                <MTable
                    stateKey="Users"
                    columns={columns}
                    data={users}
                    loading={usersLoading}
                    title={null}
                    actionMenuItems={selectedActions}
                    onSelectionChange={this.handleSelectionChange}
                    addMenuItems={this.getAddUsersActions}
                    canChange={isEditable}
                    options={{
                        selection: isEditable,
                        selectionProps: {
                            disabled: true,
                        },
                    }}
                />
            </React.Fragment>
        );
    }
}

export default flowRight(withStyles(usersStyles), withAppContext)(Users);
